import React, { useState, useEffect } from 'react';
import { 
  FaTruck, 
  FaUser, 
  FaExclamationTriangle, 
  FaBuilding
} from 'react-icons/fa';
import { vehiclesAPI, driversAPI, companiesAPI, alertsAPI } from '../../services/api'; // Import from api.js

const StatsCards = () => {
  const [stats, setStats] = useState({
    vehicles: { total: 0, available: 0, onTrip: 0, maintenance: 0 },
    drivers: { total: 0, active: 0, onDuty: 0, available: 0 },
    companies: { total: 0, active: 0 },
    alerts: { total: 0, critical: 0, high: 0, medium: 0 },
    loading: true
  });

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      setStats(prev => ({ ...prev, loading: true }));

      // Use the API from api.js
      const [vehiclesRes, driversRes, companiesRes, alertsRes] = await Promise.all([
        vehiclesAPI.getAll().catch(() => ({ success: false, data: [] })),
        driversAPI.getAll().catch(() => ({ success: false, data: [] })),
        companiesAPI.getAll().catch(() => ({ success: false, data: [] })),
        alertsAPI.getAll().catch(() => ({ success: false, data: [] }))
      ]);

      // Process vehicles data
      let vehiclesData = [];
      if (vehiclesRes && vehiclesRes.success && Array.isArray(vehiclesRes.data)) {
        vehiclesData = vehiclesRes.data;
      } else if (Array.isArray(vehiclesRes)) {
        vehiclesData = vehiclesRes;
      }
      
      const vehiclesStats = {
        total: vehiclesData.length,
        available: vehiclesData.filter(v => v.status === 'available' || v.status === 'active').length,
        onTrip: vehiclesData.filter(v => v.status === 'on_trip' || v.status === 'on-trip').length,
        maintenance: vehiclesData.filter(v => v.status === 'maintenance').length
      };

      // Process drivers data
      let driversData = [];
      if (driversRes && driversRes.success && Array.isArray(driversRes.data)) {
        driversData = driversRes.data;
      } else if (Array.isArray(driversRes)) {
        driversData = driversRes;
      } else if (driversRes && Array.isArray(driversRes.data)) {
        driversData = driversRes.data;
      }
      
      const driversStats = {
        total: driversData.length,
        active: driversData.filter(d => d.status === 'active').length,
        onDuty: driversData.filter(d => d.status === 'on_duty' || d.status === 'on-duty').length,
        available: driversData.filter(d => d.availability === 'available').length
      };

      // Process companies data
      let companiesData = [];
      if (companiesRes && companiesRes.success && Array.isArray(companiesRes.data)) {
        companiesData = companiesRes.data;
      } else if (Array.isArray(companiesRes)) {
        companiesData = companiesRes;
      }
      
      const companiesStats = {
        total: companiesData.length,
        active: companiesData.filter(c => c.status === 'active' || c.isActive === true).length
      };

      // Process alerts data
      let alertsData = [];
      if (alertsRes && alertsRes.success && Array.isArray(alertsRes.data)) {
        alertsData = alertsRes.data;
      } else if (Array.isArray(alertsRes)) {
        alertsData = alertsRes;
      }
      
      const alertsStats = {
        total: alertsData.length,
        critical: alertsData.filter(a => a.severity === 'critical').length,
        high: alertsData.filter(a => a.severity === 'high').length,
        medium: alertsData.filter(a => a.severity === 'medium').length
      };

      setStats({
        vehicles: vehiclesStats,
        drivers: driversStats,
        companies: companiesStats,
        alerts: alertsStats,
        loading: false
      });

    } catch (error) {
      console.error('Error fetching stats:', error);
      setStats(prev => ({ ...prev, loading: false }));
    }
  };

  const statsCards = [
    {
      title: 'Total Vehicles',
      value: stats.vehicles.total.toString(),
      subtitle: `${stats.vehicles.available} available • ${stats.vehicles.onTrip} on trip`,
      icon: <FaTruck className="text-xl" />,
      color: 'blue'
    },
    {
      title: 'Total Drivers',
      value: stats.drivers.total.toString(),
      subtitle: `${stats.drivers.active} active • ${stats.drivers.onDuty} on duty`,
      icon: <FaUser className="text-xl" />,
      color: 'green'
    },
    {
      title: 'Total Companies',
      value: stats.companies.total.toString(),
      subtitle: `${stats.companies.active} active`,
      icon: <FaBuilding className="text-xl" />,
      color: 'purple'
    },
    {
      title: 'Active Alerts',
      value: stats.alerts.total.toString(),
      subtitle: `${stats.alerts.critical} critical • ${stats.alerts.high} high`,
      icon: <FaExclamationTriangle className="text-xl" />,
      color: 'red'
    }
  ];

  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600 border-blue-200',
    green: 'bg-green-50 text-green-600 border-green-200',
    red: 'bg-red-50 text-red-600 border-red-200',
    purple: 'bg-purple-50 text-purple-600 border-purple-200'
  };

  const iconBgClasses = {
    blue: 'bg-blue-100',
    green: 'bg-green-100',
    red: 'bg-red-100',
    purple: 'bg-purple-100'
  };

  if (stats.loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="bg-white rounded-lg shadow-sm border p-6 animate-pulse">
            <div className="flex items-center justify-between">
              <div className="space-y-2">
                <div className="h-4 bg-gray-200 rounded w-24"></div>
                <div className="h-8 bg-gray-200 rounded w-16"></div>
                <div className="h-3 bg-gray-200 rounded w-32"></div>
              </div>
              <div className="h-12 w-12 bg-gray-200 rounded-lg"></div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      {statsCards.map((stat, index) => (
        <div 
          key={index} 
          className={`bg-white rounded-lg shadow-sm border p-5 hover:shadow-md transition-shadow duration-200 ${colorClasses[stat.color]}`}
        >
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-600 mb-1">{stat.title}</p>
              <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
              <p className="text-xs text-gray-500 mt-2">{stat.subtitle}</p>
            </div>
            
            <div className={`p-3 rounded-lg ${iconBgClasses[stat.color]} ml-4`}>
              <div className="text-gray-700">
                {stat.icon}
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default StatsCards;